import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlarmClock, ClipboardList } from 'lucide-react';
import { dateFr } from './shared';

export const COULEURS_CONSTAT = {
  conforme: 'bg-emerald-100 text-emerald-800', point_fort: 'bg-sky-100 text-sky-800', piste: 'bg-amber-100 text-amber-800',
  nc_mineure: 'bg-orange-100 text-orange-800', nc_majeure: 'bg-red-100 text-red-800',
};
const FILTRES = [['', 'Toutes'], ['nc_majeure,nc_mineure', 'Non-conformités'], ['piste', "Pistes d'amélioration"], ['point_fort', 'Points forts'], ['conforme', 'Conformes']];

const LigneNote = ({ n, onOpen }) => (
  <li className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`recap-note-${n.note_id}`}>
    <div className="mb-1 flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => onOpen(n.num)} className="font-semibold text-[#0F172A] underline-offset-2 hover:underline" data-testid={`recap-note-ouvrir-${n.note_id}`}>
        Indicateur {n.num} · {n.titre}
      </button>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COULEURS_CONSTAT[n.constat]}`}>{n.constat_label}</span>
      <span className="flex-1 text-right text-xs text-slate-400">Audit du {dateFr(n.date)} · {n.par_nom}</span>
    </div>
    <p className="whitespace-pre-wrap text-slate-700">{n.note}</p>
  </li>
);

// Onglet « Notes d'audit » : toutes les notes, filtrables par constat, groupées par année
export const RecapNotesAudit = ({ onOpen }) => {
  const [filtre, setFiltre] = useState('');
  const [data, setData] = useState(null);
  const charger = useCallback(() => {
    axios.get('/qualiopi/notes-audit', { params: filtre ? { constat: filtre } : {} }).then(({ data: d }) => setData(d)).catch(() => setData({ notes: [], compteurs: {} }));
  }, [filtre]);
  useEffect(() => { charger(); }, [charger]);
  const compte = (f) => (f ? f.split(',').reduce((s, k) => s + (data?.compteurs?.[k] || 0), 0) : Object.values(data?.compteurs || {}).reduce((s, v) => s + v, 0));
  const parAnnee = (data?.notes || []).reduce((acc, n) => { (acc[n.date.slice(0, 4)] ||= []).push(n); return acc; }, {});
  return (
    <section className="space-y-4" data-testid="qualiopi-recap-notes">
      <div className="flex flex-wrap gap-2">
        {FILTRES.map(([v, l]) => (
          <button key={l} type="button" onClick={() => setFiltre(v)} data-testid={`recap-filtre-${v || 'toutes'}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${filtre === v ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
            {l} ({compte(v)})
          </button>
        ))}
      </div>
      {!data ? <p className="text-sm text-slate-400">Chargement…</p> : data.notes.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid="recap-vide">
          <ClipboardList className="mx-auto mb-2 h-6 w-6" /> Aucune note d'audit. Ajoutez-les depuis chaque indicateur.
        </p>
      ) : Object.keys(parAnnee).sort().reverse().map(a => (
        <div key={a} className="space-y-2">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Audits {a}</h3>
          <ul className="space-y-2">{parAnnee[a].map(n => <LigneNote key={n.note_id} n={n} onOpen={onOpen} />)}</ul>
        </div>
      ))}
    </section>
  );
};

// Bandeau de rappel quand le prochain audit approche (6 semaines), avec les points du dernier audit
export const RappelAudit = ({ onOpen }) => {
  const [r, setR] = useState(null);
  useEffect(() => { axios.get('/qualiopi/rappel-audit').then(({ data }) => setR(data)).catch(() => {}); }, []);
  if (!r?.prochain_audit || r.jours_restants < 0 || r.jours_restants > 42) return null;
  const dates = r.prochain_audit_fin ? `du ${dateFr(r.prochain_audit)} au ${dateFr(r.prochain_audit_fin)}` : `le ${dateFr(r.prochain_audit)}`;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="qualiopi-rappel-audit">
      <p className="flex flex-wrap items-center gap-2 font-semibold text-amber-900">
        <AlarmClock className="h-5 w-5" /> Prochain audit {dates} : dans {r.jours_restants} jour{r.jours_restants > 1 ? 's' : ''}
      </p>
      {r.points.length === 0 ? <p className="mt-1 text-sm text-amber-800">Aucune piste ni non-conformité notée au dernier audit.</p> : (
        <>
          <p className="mt-1 text-sm text-amber-800">À vérifier, noté lors du dernier audit :</p>
          <ul className="mt-2 space-y-1">
            {r.points.map(p => (
              <li key={p.note_id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COULEURS_CONSTAT[p.constat]}`}>{p.constat_label}</span>
                <button type="button" onClick={() => onOpen(p.num)} className="font-medium text-[#0F172A] hover:underline" data-testid={`rappel-point-${p.note_id}`}>Indicateur {p.num}</button>
                <span className="text-slate-700">{p.note}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
